import { forwardRef } from 'react';
import { useTranslations } from 'next-intl';
import { IProduct, IProductSectionName } from '@monofront/types';
import ProductNutritionalValues from '../ProductNutritionalValues';

interface IProductPresentationProps {
  product: IProduct;
  name: IProductSectionName;
}

const ProductPresentation = forwardRef<
  HTMLHeadingElement,
  IProductPresentationProps
>(({ product, name }, ref) => {
  const tProducts = useTranslations('Products');

  const description = product.meta.marketing?.short_description;
  const ingredients = product.meta.marketing?.ingredients;
  const allergen = product.meta.marketing?.allergen;
  const informations = product.meta.marketing?.product_values;
  const preservation = product.meta.marketing?.preservation_advices;
  const packaging = product.meta.logistics_dimensions?.packaging;

  return (
    <>
      <h2
        ref={ref}
        id="section-presentation"
        className="section-title tw-lf-title-sm tw-mb-6 tw-mt-14 desktop:tw-lf-title-md"
        data-menu={name.menuTitle ? name.menuTitle : tProducts('presentation')}
        tabIndex={-1}
      >
        {name.sectionTitle
          ? name.sectionTitle.replace('%PRODUCT_NAME%', product.title)
          : tProducts('presentation')}
      </h2>
      {description && (
        <>
          <h3 className="tw-lf-body-xbold-md tw-mb-2 tw-mt-6">
            {tProducts('description')}
          </h3>
          <div
            className="tw-lf-rich-text"
            dangerouslySetInnerHTML={{
              __html: description,
            }}
          />
        </>
      )}

      {ingredients && (
        <>
          <h3 className="tw-lf-body-xbold-md tw-mb-2 tw-mt-6">
            {tProducts('ingredients')}
          </h3>
          <div
            className="tw-lf-rich-text"
            dangerouslySetInnerHTML={{
              __html: ingredients,
            }}
          />
        </>
      )}

      {allergen && (
        <>
          <h3 className="tw-lf-body-xbold-md tw-mb-2 tw-mt-6">
            {tProducts('allergens')}
          </h3>
          <div
            className="tw-lf-rich-text"
            dangerouslySetInnerHTML={{
              __html: allergen,
            }}
          />
        </>
      )}

      {product.meta.marketing && (
        <ProductNutritionalValues
          product={product}
          marketing={product.meta.marketing}
          unitOfMeasurement={product.meta.finance?.unit_of_measurement}
          tags={product.tags}
        />
      )}

      {informations && (
        <>
          <h3 className="tw-lf-body-xbold-md tw-mb-2 tw-mt-6">
            {tProducts('informations')}
          </h3>
          <div
            className="tw-lf-rich-text"
            dangerouslySetInnerHTML={{
              __html: informations,
            }}
          />
        </>
      )}

      {preservation && (
        <>
          <h3 className="tw-lf-body-xbold-md tw-mb-2 tw-mt-6">
            {tProducts('preservation')}
          </h3>
          <div
            className="tw-lf-rich-text"
            dangerouslySetInnerHTML={{
              __html: preservation,
            }}
          />
        </>
      )}

      {packaging && (
        <>
          <h3 className="tw-lf-body-xbold-md tw-mb-2 tw-mt-6">
            {tProducts('packaging')}
          </h3>
          <div
            className="tw-lf-rich-text"
            dangerouslySetInnerHTML={{
              __html: packaging,
            }}
          />
        </>
      )}
    </>
  );
});

export default ProductPresentation;
